import React, { useEffect, useState } from "react";
import { User } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { LeagueGwStats, fetchLeagueGwStats } from "../../api/leagues";
import ClubBadge from "../ClubBadge";
import { getClubBadge } from "../../data/clubBadges";
import { HIGHLIGHT_COLOR } from "./fixtureUtils";
import { panelHeading } from "../../styles";

interface Props {
  user: User;
  leagueId: string;
  resultsGwId: string | null;
  /** Players picked by the logged-in member in a Syndicate player pot —
   * rendered in the same purple used for their picked clubs in the
   * fixtures panel. */
  highlightedPlayerIds?: Set<string>;
}

type Tab = "GOALS" | "ASSISTS" | "CARDS";

const PAGE_SIZE = 10;

const panelStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 12,
  marginBottom: "1rem",
  overflow: "hidden",
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "0 0 0.4rem",
  fontSize: "0.6rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: active ? "var(--color-accent)" : "var(--color-text-dim)",
  borderBottom: active ? "2px solid var(--color-accent)" : "2px solid transparent",
  fontFamily: "var(--font-body)",
});

const cardChip = (kind: "yellow" | "red"): React.CSSProperties => ({
  fontSize: "0.7rem",
  fontWeight: 700,
  padding: "1px 6px",
  borderRadius: 99,
  color: kind === "yellow" ? "var(--color-warning)" : "var(--color-danger)",
  background: `color-mix(in srgb, ${kind === "yellow" ? "var(--color-warning)" : "var(--color-danger)"} 15%, transparent)`,
});

function firstNonEmptyTab(data: LeagueGwStats | null): Tab {
  if (!data) return "GOALS";
  if (data.gameweek.scorers.length > 0) return "GOALS";
  if (data.gameweek.assisters.length > 0) return "ASSISTS";
  return "CARDS";
}

export default function SyndicateGwStatsPanel({ user, leagueId, resultsGwId, highlightedPlayerIds }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<LeagueGwStats | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("GOALS");
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!resultsGwId) { setData(null); return; }
    fetchLeagueGwStats(user, leagueId, resultsGwId)
      .then((res) => {
        setData(res);
        setActiveTab(firstNonEmptyTab(res));
        setPage(0);
      })
      .catch(() => setData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, resultsGwId]);

  if (!data) return null;
  const { scorers, assisters, carded } = data.gameweek;
  if (scorers.length === 0 && assisters.length === 0 && carded.length === 0) return null;

  const rows: { playerId: string; playerName: string; playerClub: string; value: React.ReactNode }[] =
    activeTab === "GOALS"
      ? scorers.map((s) => ({ playerId: s.playerId, playerName: s.playerName, playerClub: s.playerClub, value: `${s.goals} ${s.goals === 1 ? t("fixtures.goal") : t("fixtures.goals")}` }))
      : activeTab === "ASSISTS"
      ? assisters.map((s) => ({ playerId: s.playerId, playerName: s.playerName, playerClub: s.playerClub, value: `${s.assists} ${s.assists === 1 ? t("fixtures.assist") : t("fixtures.assists")}` }))
      : carded.map((s) => ({
          playerId: s.playerId,
          playerName: s.playerName,
          playerClub: s.playerClub,
          value: (
            <span style={{ display: "inline-flex", gap: 4 }}>
              {s.yellowCards > 0 && <span style={cardChip("yellow")}>{s.yellowCards}Y</span>}
              {s.redCards > 0 && <span style={cardChip("red")}>{s.redCards}R</span>}
            </span>
          ),
        }));

  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const switchTab = (tab: Tab) => { setActiveTab(tab); setPage(0); };

  return (
    <div style={panelStyle}>
      <div style={{ padding: "0.75rem 1.25rem 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ ...panelHeading, margin: 0 }}>
          {t("fixtures.gwStatsTitle")} · {data.gameweek.name}
        </h2>
        {totalPages > 1 && (
          <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)" }}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rows.length)} of {rows.length}
          </span>
        )}
      </div>

      <div style={{ padding: "0.5rem 1.25rem 0", display: "flex", gap: "0.75rem" }}>
        {scorers.length > 0 && <button onClick={() => switchTab("GOALS")} style={tabStyle(activeTab === "GOALS")}>{t("fixtures.tabGoals")}</button>}
        {assisters.length > 0 && <button onClick={() => switchTab("ASSISTS")} style={tabStyle(activeTab === "ASSISTS")}>{t("fixtures.tabAssists")}</button>}
        {carded.length > 0 && <button onClick={() => switchTab("CARDS")} style={tabStyle(activeTab === "CARDS")}>{t("fixtures.tabCards")}</button>}
      </div>

      <div style={{ marginTop: "0.5rem" }}>
        {pageRows.map((row) => {
          const cfg = getClubBadge(row.playerClub);
          const isPicked = highlightedPlayerIds?.has(row.playerId) ?? false;
          return (
            <div key={row.playerId} style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0.45rem 1.25rem",
              borderBottom: "1px solid var(--color-border-subtle)",
              background: isPicked ? "color-mix(in srgb, oklch(0.72 0.18 295) 8%, transparent)" : undefined,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {cfg && <ClubBadge config={cfg} size={16} title={row.playerClub} />}
                <span style={{ fontSize: "0.85rem", fontWeight: isPicked ? 700 : 600, color: isPicked ? HIGHLIGHT_COLOR : "var(--color-text)" }}>{row.playerName}</span>
              </div>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-accent)", minWidth: 28, textAlign: "right" }}>
                {row.value}
              </span>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.4rem 1.25rem" }}>
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
            style={{
              background: "none",
              border: "none",
              cursor: page === 0 ? "default" : "pointer",
              fontSize: "0.7rem",
              fontWeight: 700,
              color: page === 0 ? "var(--color-border)" : "var(--color-accent)",
              padding: "0.2rem 0",
            }}
          >
            {t("fixtures.prev")}
          </button>
          <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)" }}>
            {page + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages - 1}
            style={{
              background: "none",
              border: "none",
              cursor: page === totalPages - 1 ? "default" : "pointer",
              fontSize: "0.7rem",
              fontWeight: 700,
              color: page === totalPages - 1 ? "var(--color-border)" : "var(--color-accent)",
              padding: "0.2rem 0",
            }}
          >
            {t("fixtures.next")}
          </button>
        </div>
      )}
    </div>
  );
}
